<script lang="ts">
  /**
   * The barogram, and the scrubber that drives the map.
   *
   * Inline SVG rather than a charting library: it is one path, one axis and a cursor, and the
   * only hard part — mapping a pointer position back to a track index — is three lines that a
   * library would wrap rather than remove.
   *
   * The path is drawn from *every* fix, not a sample. A five-hour flight is a few thousand
   * points, which is a large `d` attribute and a perfectly ordinary one for a browser; dropping
   * points would smooth away exactly the sharp climbs the graph is read for.
   */
  import { climb, localTime, metres } from './format.ts';
  import type { CloudProfile, TerrainProfile, TrackFeature } from './api.ts';

  interface Props {
    track: TrackFeature;
    cursor: number;
    height?: number;
    /**
     * Ground elevation sampled along the track, if it could be fetched.
     *
     * Optional and allowed to be absent: the chart is complete without it, and it arrives from
     * a third-party elevation service that may not answer.
     */
    terrain?: TerrainProfile | undefined;
    /**
     * Estimated cloud base and cover per hour, or undefined.
     *
     * The base is a guess from the temperature/dew-point spread — see the daemon's
     * `cloudsForFlight` — and it errs optimistic. Drawn as a dashed line and a soft band for
     * exactly that reason: a hard edge would be a claim the data does not support.
     */
    clouds?: CloudProfile | undefined;
  }

  let { track, cursor = $bindable(0), height = 150, terrain, clouds }: Props = $props();

  let svg: SVGSVGElement;

  /**
   * The measured width, because the viewBox is in real pixels rather than in units of its own.
   *
   * It used to be a fixed `0 0 1000 150` box scaled to whatever width there was. On a laptop
   * that is close enough to 1:1 that nobody notices; on a 390 px phone the scale factor is
   * 0.35, and everything inside went with it — an eleven-pixel altitude label rendered at
   * four, and the whole chart was a 52-pixel strip. Neither is legible, and neither can be
   * fixed by styling, because the shrinking happens after the CSS.
   *
   * Measuring instead means one pixel in here is one pixel on the glass, at any width.
   */
  let boxW = $state(0);

  const times = $derived(track.properties.times);
  const altitudes = $derived(track.properties.altitudes);

  const t0 = $derived(times[0] ?? 0);
  const t1 = $derived(times[times.length - 1] ?? 1);
  const span = $derived(Math.max(1, t1 - t0));

  const known = $derived(altitudes.filter((a): a is number => a !== null));
  /**
   * The ground line, expanded from the samples to every track index.
   *
   * The elevation service answers a hundred points and the track has thousands, so the samples
   * are linearly interpolated between. That is honest at this scale — the chart is 600 pixels
   * wide and a hundred samples is already finer than it can draw — and it is why the profile is
   * drawn as a soft fill rather than a crisp line that would imply a precision it does not have.
   */
  const groundAt = $derived.by(() => {
    if (terrain === undefined || !terrain.available || terrain.indices.length === 0) return null;
    const idx = terrain.indices;
    const el = terrain.elevations;
    return (i: number): number => {
      if (i <= (idx[0] ?? 0)) return el[0] ?? 0;
      for (let k = 1; k < idx.length; k++) {
        const a = idx[k - 1];
        const b = idx[k];
        if (a === undefined || b === undefined) continue;
        if (i <= b) {
          const span_ = b - a;
          const f = span_ === 0 ? 0 : (i - a) / span_;
          return (el[k - 1] ?? 0) + ((el[k] ?? 0) - (el[k - 1] ?? 0)) * f;
        }
      }
      return el[el.length - 1] ?? 0;
    };
  });

  /** Height above the ground under the cursor, which is the number a pilot actually thinks in. */
  const aglAtCursor = $derived.by(() => {
    const g = groundAt;
    const alt = altitudes[cursor];
    if (g === null || alt === null || alt === undefined) return null;
    return Math.max(0, Math.round(alt - g(cursor)));
  });

  const minAlt = $derived(known.length > 0 ? Math.min(...known) : 0);
  const maxAlt = $derived(known.length > 0 ? Math.max(...known) : 1000);
  // A little headroom, and never a zero-height band for a flight that held one altitude.
  // The floor has to include the terrain, or the ground profile is drawn below the axis and
  // the aircraft appears to fly underground.
  const minGround = $derived.by(() => {
    if (terrain === undefined || !terrain.available || terrain.elevations.length === 0) {
      return Number.POSITIVE_INFINITY;
    }
    return Math.min(...terrain.elevations);
  });
  const lo = $derived(Math.floor((Math.min(minAlt, minGround) - 50) / 100) * 100);
  const hi = $derived(Math.max(lo + 100, Math.ceil((maxAlt + 100) / 100) * 100));

  // 1000 only until the first measurement lands, so the very first frame is not degenerate.
  const VIEW_W = $derived(boxW > 0 ? boxW : 1000);
  const PAD_L = 46;
  const PAD_R = 8;
  const PAD_T = 8;
  const PAD_B = 20;

  /**
   * How tall the chart actually is.
   *
   * `height` is the figure for a wide screen. Narrow gets *more*, not less: a barogram is read
   * for its shape — the climbs, the glides, the save at 400 m — and vertical room is what
   * carries that. A phone has height to spare and no width, which is the opposite of the
   * trade the old fixed aspect ratio made.
   */
  const VIEW_H = $derived(boxW > 0 && boxW < 560 ? 210 : height);

  const plotW = $derived(VIEW_W - PAD_L - PAD_R);
  const plotH = $derived(VIEW_H - PAD_T - PAD_B);

  const xOf = (t: number) => PAD_L + ((t - t0) / span) * plotW;
  const yOf = (alt: number) => PAD_T + plotH - ((alt - lo) / (hi - lo)) * plotH;

  /**
   * The trace, as a filled area plus its outline.
   *
   * A gap in reception becomes a break in the path rather than a straight line across it: a
   * ruler-straight descent through a twenty-minute hole is the single most misleading thing a
   * barogram can draw.
   */
  /**
   * The cloud base across the flight, and how solid the cloud above it was.
   *
   * Interpolated between the hourly values rather than drawn as steps: the base does not jump
   * at the top of the hour, and a staircase would suggest the model knows more than it does.
   * Hours the model had nothing for break the line instead of being bridged.
   *
   * The band above the line is shaded by cover, so a scattered day is a hint and an overcast
   * one is a lid. Cover is taken as the greater of low and mid — a solid mid deck is what
   * stops a climb even when the low cloud is broken.
   */
  const cloudBand = $derived.by(() => {
    const hours = clouds?.hours ?? [];
    if (!clouds?.available || hours.length === 0) return null;

    const usable = hours.filter((h) => h.baseM !== null);
    if (usable.length === 0) return null;

    const top = PAD_T;
    const segments: Array<{ line: string; area: string; cover: number }> = [];
    for (let i = 0; i + 1 < usable.length; i++) {
      const a = usable[i];
      const b = usable[i + 1];
      if (a === undefined || b === undefined) continue;
      // A gap of more than two hours is not something to draw a line across.
      if (b.t - a.t > 7200) continue;

      const ax = xOf(a.t);
      const bx = xOf(b.t);
      const ay = yOf(a.baseM as number);
      const by = yOf(b.baseM as number);
      const cover = Math.max(a.cover ?? 0, a.coverLow ?? 0, a.coverMid ?? 0) / 100;
      segments.push({
        line: `M${ax.toFixed(1)},${ay.toFixed(1)}L${bx.toFixed(1)},${by.toFixed(1)}`,
        area: `M${ax.toFixed(1)},${ay.toFixed(1)}L${bx.toFixed(1)},${by.toFixed(1)}L${bx.toFixed(1)},${top}L${ax.toFixed(1)},${top}Z`,
        cover,
      });
    }
    if (segments.length === 0) return null;

    const first = usable[0];
    const last = usable[usable.length - 1];
    return {
      segments,
      // For the legend line under the chart.
      lowestM: Math.min(...usable.map((h) => h.baseM as number)),
      highestM: Math.max(...usable.map((h) => h.baseM as number)),
      fromT: first?.t ?? t0,
      toT: last?.t ?? t1,
    };
  });

  /** The terrain as a closed area from the bottom of the plot up to the ground. */
  const terrainPath = $derived.by(() => {
    const g = groundAt;
    if (g === null || times.length === 0) return null;
    const bottom = (PAD_T + plotH).toFixed(1);
    let d = `M${xOf(times[0] ?? t0).toFixed(1)},${bottom}`;
    // One point per sample, not per fix: the profile is interpolated anyway and a path with
    // three thousand nodes costs more than it shows.
    const step = Math.max(1, Math.floor(times.length / 240));
    for (let i = 0; i < times.length; i += step) {
      const t = times[i];
      if (t === undefined) continue;
      d += `L${xOf(t).toFixed(1)},${yOf(g(i)).toFixed(1)}`;
    }
    const lastT = times[times.length - 1];
    if (lastT !== undefined) {
      d += `L${xOf(lastT).toFixed(1)},${yOf(g(times.length - 1)).toFixed(1)}`;
      d += `L${xOf(lastT).toFixed(1)},${bottom}Z`;
    }
    return d;
  });

  const paths = $derived.by(() => {
    const segments: string[] = [];
    let current = '';

    for (let i = 0; i < times.length; i++) {
      const alt = altitudes[i];
      const t = times[i];
      if (alt === null || alt === undefined || t === undefined) {
        if (current.length > 0) segments.push(current);
        current = '';
        continue;
      }
      const gap = i > 0 && (t - (times[i - 1] ?? t)) > 60;
      if (gap && current.length > 0) {
        segments.push(current);
        current = '';
      }
      current += `${current.length === 0 ? 'M' : 'L'}${xOf(t).toFixed(1)},${yOf(alt).toFixed(1)}`;
    }
    if (current.length > 0) segments.push(current);
    return segments;
  });

  const areaPath = $derived.by(() => {
    const first = paths[0];
    if (first === undefined) return '';
    const baseline = (PAD_T + plotH).toFixed(1);
    // Fill under the first continuous segment only. Filling across a gap would put ink where
    // there is no data, which is the thing the broken line exists to avoid.
    const start = first.slice(1).split('L')[0] ?? '';
    const startX = start.split(',')[0] ?? String(PAD_L);
    return `${first}L${xEndOf(first)},${baseline}L${startX},${baseline}Z`;
  });

  function xEndOf(path: string): string {
    const last = path.split('L').pop() ?? '';
    return last.split(',')[0] ?? String(PAD_L);
  }

  /** Round numbers on the altitude axis, four of them, whatever the range. */
  const ticks = $derived.by(() => {
    const step = niceStep((hi - lo) / 4);
    const out: number[] = [];
    for (let v = Math.ceil(lo / step) * step; v <= hi; v += step) out.push(v);
    return out;
  });

  function niceStep(raw: number): number {
    const magnitude = 10 ** Math.floor(Math.log10(Math.max(1, raw)));
    for (const factor of [1, 2, 2.5, 5, 10]) {
      if (magnitude * factor >= raw) return magnitude * factor;
    }
    return magnitude * 10;
  }

  const cursorT = $derived(times[cursor] ?? t0);
  const cursorAlt = $derived(altitudes[cursor] ?? null);

  const cursorClimb = $derived.by(() => {
    // Over ~20 s, the same window the daemon uses for its own best-climb figure, so the two
    // numbers describe the same thing.
    const target = cursorT - 20;
    let j = cursor;
    while (j > 0 && (times[j] ?? 0) > target) j -= 1;
    const from = altitudes[j];
    const to = altitudes[cursor];
    const dt = cursorT - (times[j] ?? cursorT);
    if (from === null || from === undefined || to === null || to === undefined || dt <= 0) {
      return null;
    }
    return (to - from) / dt;
  });

  /** Pointer position to track index. Binary search, because the times are sorted. */
  function indexAt(clientX: number): number {
    const rect = svg.getBoundingClientRect();
    const ratio = (clientX - rect.left) / rect.width;
    const t = t0 + Math.max(0, Math.min(1, (ratio * VIEW_W - PAD_L) / plotW)) * span;

    let lowIndex = 0;
    let highIndex = times.length - 1;
    while (lowIndex < highIndex) {
      const mid = (lowIndex + highIndex) >> 1;
      if ((times[mid] ?? 0) < t) lowIndex = mid + 1;
      else highIndex = mid;
    }
    return lowIndex;
  }

  let scrubbing = $state(false);

  function onPointer(event: PointerEvent): void {
    if (event.type === 'pointerdown') {
      scrubbing = true;
      svg.setPointerCapture(event.pointerId);
    }
    if (!scrubbing && event.type === 'pointermove') return;
    cursor = indexAt(event.clientX);
  }

  function onKey(event: KeyboardEvent): void {
    // The scrubber is reachable and usable from a keyboard: a barogram nobody can step through
    // one fix at a time is a picture, not an instrument.
    const step = event.shiftKey ? 60 : 1;
    if (event.key === 'ArrowRight') cursor = Math.min(times.length - 1, cursor + step);
    else if (event.key === 'ArrowLeft') cursor = Math.max(0, cursor - step);
    else if (event.key === 'Home') cursor = 0;
    else if (event.key === 'End') cursor = times.length - 1;
    else return;
    event.preventDefault();
  }
</script>

<div class="baro" bind:clientWidth={boxW}>
  <svg
    bind:this={svg}
    viewBox="0 0 {VIEW_W} {VIEW_H}"
    style:height="{VIEW_H}px"
    preserveAspectRatio="none"
    role="slider"
    tabindex="0"
    aria-label="Barogramm, Position im Flug"
    aria-valuemin="0"
    aria-valuemax={times.length - 1}
    aria-valuenow={cursor}
    aria-valuetext="{localTime(cursorT)}, {metres(cursorAlt)}"
    onpointerdown={onPointer}
    onpointermove={onPointer}
    onpointerup={() => (scrubbing = false)}
    onpointercancel={() => (scrubbing = false)}
    onkeydown={onKey}
  >
    <defs>
      <linearGradient id="baro-fill" x1="0" y1="0" x2="0" y2="1">
        <stop offset="0%" stop-color="var(--track)" stop-opacity="0.34" />
        <stop offset="100%" stop-color="var(--track)" stop-opacity="0.02" />
      </linearGradient>
    </defs>

    {#each ticks as tick (tick)}
      <line
        x1={PAD_L}
        x2={VIEW_W - PAD_R}
        y1={yOf(tick)}
        y2={yOf(tick)}
        stroke="var(--line)"
        stroke-width="1"
        vector-effect="non-scaling-stroke"
      />
      <text x={PAD_L - 6} y={yOf(tick) + 3.5} text-anchor="end" class="tick">{tick}</text>
    {/each}

    {#if cloudBand !== null}
      <!-- Above the trace but under it in reading order: the sky is the context, not the
           subject. Opacity carries the cover, so a scattered day is a hint and an overcast one
           a lid. -->
      {#each cloudBand.segments as seg, i (i)}
        <path d={seg.area} fill="rgba(198, 210, 226, {0.05 + seg.cover * 0.22})" />
      {/each}
      {#each cloudBand.segments as seg, i (i)}
        <path
          d={seg.line}
          fill="none"
          stroke="rgba(198, 210, 226, 0.55)"
          stroke-width="1.2"
          stroke-dasharray="5 4"
          vector-effect="non-scaling-stroke"
        />
      {/each}
    {/if}

    {#if terrainPath !== null}
      <!-- Under everything else: it is the ground. -->
      <path d={terrainPath} fill="var(--terrain-fill, rgba(140, 150, 130, 0.28))" />
      <path
        d={terrainPath}
        fill="none"
        stroke="var(--terrain-line, rgba(170, 180, 155, 0.55))"
        stroke-width="1"
      />
    {/if}
    <path d={areaPath} fill="url(#baro-fill)" />

    {#each paths as segment, i (i)}
      <path
        d={segment}
        fill="none"
        stroke="var(--track)"
        stroke-width="1.8"
        stroke-linejoin="round"
        vector-effect="non-scaling-stroke"
      />
    {/each}

    <line
      x1={xOf(cursorT)}
      x2={xOf(cursorT)}
      y1={PAD_T}
      y2={PAD_T + plotH}
      stroke="var(--accent)"
      stroke-width="1.5"
      vector-effect="non-scaling-stroke"
    />
    {#if cursorAlt !== null}
      <circle cx={xOf(cursorT)} cy={yOf(cursorAlt)} r="4" fill="var(--accent)" />
    {/if}
  </svg>

  <div class="readout num">
    <div><span class="faint">Zeit</span> {localTime(cursorT)}</div>
    <div><span class="faint">Höhe</span> {metres(cursorAlt)}</div>
    {#if aglAtCursor !== null}
      <!-- The number a pilot actually flies by. 800 m over the Alb is 20 m above a ridge or
           500 m above the Neckar valley, and the MSL figure alone cannot tell you which. -->
      <div><span class="faint">über Grund</span> {aglAtCursor} m</div>
    {/if}
    <div><span class="faint">Steigen</span> {climb(cursorClimb)}</div>
    <div>
      <span class="faint">Fahrt</span>
      {track.properties.speeds[cursor] === null || track.properties.speeds[cursor] === undefined
        ? '–'
        : `${Math.round(track.properties.speeds[cursor] ?? 0)} km/h`}
    </div>
  </div>
</div>

<style>
  .baro {
    display: flex;
    flex-direction: column;
    gap: 0.4rem;
  }

  svg {
    width: 100%;
    /* The height is set inline from the same number the viewBox uses, so the scale is 1:1 and
       an 11 px label is 11 px. `auto` would restore the aspect-ratio scaling this replaced. */
    display: block;
    touch-action: pan-y;
    cursor: crosshair;
    border-radius: var(--radius-sm);
    background: var(--bg-sunken);
  }
  svg:focus-visible {
    outline: 2px solid var(--accent);
    outline-offset: 2px;
  }

  .tick {
    font-size: 11px;
    fill: var(--text-faint);
  }

  .readout {
    display: grid;
    grid-template-columns: repeat(2, 1fr);
    gap: 0.2rem 1rem;
    font-size: 0.85rem;
  }
  .readout .faint {
    display: inline-block;
    min-width: 4.2em;
  }

  @media (min-width: 640px) {
    .readout {
      grid-template-columns: repeat(4, 1fr);
    }
  }
</style>
